import isArray from 'lodash/isArray'
import omit from 'lodash/omit'
import get from 'lodash/get'
import { useList } from '../useList'
import type { DocumentQueryResult, QueryInput } from '../types/QueryInput'
import { getOperationName } from '@apollo/client/utilities'
import { useEffect } from 'react'
import { type DocumentNode, gql } from '@apollo/client'
import { Kind, type DefinitionNode, type FieldNode, type SelectionSetNode } from 'graphql'
import camelCase from 'lodash/camelCase'
import { useBuildDocumentQueryOptions } from './useBuildDocumentQueryOptions'
import { mergeDocumentUpdate } from './mergeDocumentUpdate'

export type UseDocumentProps<T = any> = {
  _id?: string
  subscribeToUpdates?: boolean
} & QueryInput<T>

export type UseDocumentResult<T = any> = DocumentQueryResult<T>

const extractFieldsFromSelectionSet = (selectionSet: SelectionSetNode, fragmentMap: Record<string, any>): string => {
  if (!selectionSet) return ''

  return selectionSet.selections
    .map((selection: any) => {
      if (selection.kind === Kind.FRAGMENT_SPREAD) {
        const fragmentName = selection.name.value
        const fragment = fragmentMap[fragmentName]

        return fragment ? extractFieldsFromSelectionSet(fragment.selectionSet, fragmentMap) : ''
      }
      if (selection.kind === Kind.FIELD) {
        const subFields = selection.selectionSet
          ? extractFieldsFromSelectionSet(selection.selectionSet, fragmentMap)
          : ''
        return subFields ? `${selection.name.value} { ${subFields} }` : selection.name.value
      }
      return ''
    })
    .filter(Boolean)
    .join('\n')
}

const buildSubscriptionQuery = (singleQuery: DocumentNode) => {
  const operationName = getOperationName(singleQuery)

  const fragmentMap: Record<string, any> = {}

  // Store fragments for later lookup
  singleQuery.definitions.forEach((definition: DefinitionNode) => {
    if (definition.kind === Kind.FRAGMENT_DEFINITION) {
      fragmentMap[definition.name.value] = definition
    }
  })

  const operation = singleQuery.definitions.find((def) => def.kind === Kind.OPERATION_DEFINITION)

  if (!operation || operation.kind !== Kind.OPERATION_DEFINITION) {
    throw new Error('Invalid GraphQL query')
  }

  const entityField = operation.selectionSet.selections.find(
    (selection) => selection.kind === Kind.FIELD && selection.name.value === operationName
  ) as FieldNode | undefined

  if (!entityField) {
    throw new Error(
      `Could not use subscrption from useDocument because we cannot extract fields for entity: ${operationName}`
    )
  }

  const fields = extractFieldsFromSelectionSet(entityField.selectionSet, fragmentMap)

  //we have default query name as single or all, so we can extract the entity name by removing the prefix
  const entityName = operationName.replace(/^single/, '').replace(/^all/, '')
  const subscriptionName = camelCase(`updated ${entityName}`) //will be converted to "updatedOrder"

  const queryString = `
       subscription ${subscriptionName}($filter: JSON) {
          ${subscriptionName}(filter: $filter) {
              ${fields}  # Inject dynamically extracted fields
          }
      }
  `
  return {
    subscriptionQuery: gql(queryString),
    subscriptionName,
  }
}

export const useDocument = <T = any>({
  subscribeToUpdates = false,
  ...props
}: UseDocumentProps<T>): UseDocumentResult<T> => {
  const { entity, limit, skip, filter, options, query } = useBuildDocumentQueryOptions<T>(props)

  const listResult = useList<T>({
    query,
    entity,
    projection: props.projection,
    sort: props.sort,
    limit,
    skip,
    filter,
    options,
  })

  let document: any = get(listResult, 'list', null)
  if (isArray(document)) {
    document = get(listResult, 'list[0]')
  }

  const { error, loading, refetch, subscribeToMore, ...result } = omit(listResult, [
    'list',
    'pageInfo',
    'loadMoreEntries',
  ])

  useEffect(() => {
    if (!subscribeToUpdates) return

    const { subscriptionQuery, subscriptionName } = buildSubscriptionQuery(query)

    const unsubscribe = subscribeToMore({
      document: subscriptionQuery,
      variables: { filter },
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData?.data) return prev

        // we know this is a single document, so it should return as object not array just like from the list
        //even though we query from the the useList result
        return {
          [entity]: mergeDocumentUpdate(prev[entity], subscriptionData.data[subscriptionName]),
        }
      },
    })

    // tear down the previous subscription before a re-run resubscribes, so re-runs replace
    // the live subscription instead of stacking another one on top
    return unsubscribe
    // filter intentionally excluded: useBuildDocumentQueryOptions rebuilds it as a new object
    // literal on every render (`filter = { _id }`, or merged fresh even when passed through), so
    // it is never referentially stable. Cleanup now makes re-subscribing safe, but including
    // `filter` here would still fire a teardown/resubscribe on every unrelated re-render of any
    // `useDocument` consumer, not just on a real filter change — that is a new cost, not the
    // stacking bug this comment used to describe. `query` is a stable, caller-provided reference
    // (a module-level gql document) so it is safe to include.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribeToUpdates, subscribeToMore, entity, query])

  //return the result, another apollo-related result is included from useList
  //the isEmpty is handled by the list too
  return { error, loading, refetch, ...result, document, subscribeToMore }
}
